import { useFrame, useThree } from '@react-three/fiber';
import {
  forwardRef,
  memo,
  MutableRefObject,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Color, Group, Vector3 } from 'three';

import type { GroupProps } from 'types/r3f';

import Atmosphere, { AtmosphereRef } from './Atmosphere/Atmosphere';
import Globe, { GlobeRef } from './Globe/Globe';

import useSphereRadius from 'hooks/useSphereRadius';

const ATMOSPHERE_DAY_COLOR = new Color('#8cb3d9'); // A lighter, more pastel blue
const ATMOSPHERE_TWILIGHT_COLOR = new Color('#9a94b3'); // A softer, less saturated purple-gray
export const SUN_DIRECTION = new Vector3(-0.8, 0, 0);

export interface EarthRef {
  sunDirection: MutableRefObject<Vector3>;
  playOutro: () => Promise<any>;
  playWatchAgain: () => Promise<any>;
  earth: MutableRefObject<Group | null>;
}

const Earth = forwardRef<EarthRef, GroupProps>(({ ...props }, ref) => {
  const predictedRadius = useSphereRadius(10, 0.9);
  const { camera } = useThree();

  const earthRef = useRef<Group>(null!);
  const globeRef = useRef<GlobeRef>(null);
  const atmosphereRef = useRef<AtmosphereRef>(null);
  const sunDirection = useRef<Vector3>(camera.position.clone().normalize());

  useFrame(() => {
    // Kept as a safety net for when either child remounts and brings a fresh
    // material with it. Each is checked on its own: coupling them meant a
    // missing atmosphere also froze the globe's sun direction.
    if (globeRef.current?.sunDirection) {
      globeRef.current.sunDirection.value = sunDirection.current;
    }

    if (atmosphereRef.current?.sunDirection) {
      atmosphereRef.current.sunDirection.value = sunDirection.current;
    }
  });

  useImperativeHandle(ref, () => ({
    earth: earthRef,
    sunDirection,
    playOutro: () => {
      return Promise.all([
        globeRef.current?.playOutro(),
        atmosphereRef.current?.playOutro(),
      ]);
    },
    playWatchAgain: () => {
      return Promise.all([
        globeRef.current?.playWatchAgain(),
        atmosphereRef.current?.playWatchAgain(),
      ]);
    },
  }));

  const radius = useMemo(() => Math.min(predictedRadius, 4), [predictedRadius]);

  const EarthGeometry = useMemo(
    () => <sphereGeometry args={[radius, 64, 64]} />,
    [radius]
  );

  const childProps = {
    dayColor: ATMOSPHERE_DAY_COLOR,
    twilightColor: ATMOSPHERE_TWILIGHT_COLOR,
    geometry: EarthGeometry,
    // The very vector EarthScene mutates each frame, not a copy of it. The
    // children build their uSunDirection uniform out of whatever lands here,
    // so handing over a detached clone left the shaders pointing at a vector
    // nothing ever writes to — lighting then froze the moment the per-frame
    // reassignment above missed a beat, e.g. right after a remount.
    sunDirection: sunDirection.current,
  };

  return (
    <group ref={earthRef} {...props} frustumCulled={false}>
      <Globe {...childProps} ref={globeRef} />
      <Atmosphere {...childProps} ref={atmosphereRef} />
    </group>
  );
});

Earth.displayName = 'Earth';
export default memo(Earth);
